//@ts-nocheck
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CollapsibleCard from "components/CollapsibleCard";
import moment from "moment";
import { calculateWorkingDays, getCalendarDateFromBusinessDays } from "utils/CalanderUtils";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { get, post, request } from "../../../../requests";
import { useStoreState, useStoreActions } from "easy-peasy";
import { useSubmissionStore } from "models/applications/SubmissionStore";
import FundingChart from "./FundingChart";
import { convertNumeric } from "utils/CommonUtils";
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import StorageUtils from "utils/StorageUtils";
import { Add, Expand, ExpandMore, PlayCircle } from "@mui/icons-material";
import MDSelect from "components/MDSelect";
import Loader from "components/Loader/Basic";

const HeaderSection: React.FC<{ label: string; value: string; style?: any | null }> = ({
  label,
  value,
  style,
}) => (
  <MDBox display="flex" flexDirection="column" alignItems="center">
    <MDTypography
      variant="body2"
      sx={{
        color: "#7B809A",
        textAlign: "center",
        fontFamily: "Roboto",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "140%",
      }}
    >
      {label}
    </MDTypography>
    <MDTypography
      variant="h6"
      sx={{
        color: "#344767",
        textAlign: "center",
        fontFamily: "Roboto",
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "140%",
        ...style,
      }}
    >
      {value}
    </MDTypography>
  </MDBox>
);

interface SummaryCardProps {
  dealApplicationId: any;
  allowButton: Boolean;
  allowResubmit: Boolean;
  onResubmit: () => void;
  allowSummarySelection: any;
  permittedTabs: any;
  refreshDataBasedOnSource: (source: string) => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  dealApplicationId,
  allowButton = true,
  allowSummarySelection = true,
  allowResubmit = false,
  onResubmit = undefined,
  permittedTabs,
  refreshDataBasedOnSource,
}) => {
  const [open, setOpen] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("weekly");

  const bankStatementsSummary = useStoreState(
    (state: any) => state.bankSummaryStore.bankStatementData
  );
  const advancesData = useStoreState((state: any) => state.bankSummaryStore.advancesData);
  const scoreboard = useStoreState((state: any) => state.bankSummaryStore.scoreboard);
  const setScoreboard = useStoreActions((actions: any) => actions.bankSummaryStore.setScoreboard);
  const positionCount = useStoreState((state: any) => state.bankSummaryStore.positionCount);
  const isAnalysisLoaded = useStoreState((state: any) => state.dealApplication.isAnalysisLoaded);
  const [scoreboardLoading, setScoreboardLoading] = useState(false);

  const [selectedSource, setSelectedSource] = useState<any>("");
  const contractTerms = useStoreState((state: any) => state.offerStore.contractTerms);
  const [AVAILABLE_HOLDBACK, SET_AVAILABLE_HOLDBACK] = useState(
    contractTerms?.contractTerms?.funder?.maxHoldback || 0
  );
  const { setSelectedTab } = useStoreActions((actions: any) => actions.dealApplication);
  const { selectedAnalysisTab } = useStoreState((state: any) => state.dealApplication);
  const { prevSelectedAnalysisTab } = useStoreState((state: any) => state.dealApplication);

  const tabLabels = [
    ...(StorageUtils.isFeatureEnabled("smartScrub") ? ["SmartScrub"] : []),
    ...(permittedTabs["decisionLogic"] !== "none" && StorageUtils.isFeatureEnabled("decision_logic")
      ? ["Decision Logic"]
      : []),
    ...(StorageUtils.isFeatureEnabled("lendPathway") ? ["Lend Pathway"] : []),
    ...(StorageUtils.isFeatureEnabled("moneyThumb") ? ["MoneyThumb"] : []),
    ...(StorageUtils.isFeatureEnabled("ocrolus") ? ["Ocrolus"] : []),
    ...(StorageUtils.isFeatureEnabled("hyperVerge") ? ["HyperVerge"] : []),
  ];

  const tabOptions = tabLabels
    .map((label, index) => ({
      label,
      value: index,
    }))
    .filter((option) => option.label !== "Decision Logic");

  const [formData, setFormData] = useState({
    fundingAmount: 0,
    factor: 0,
    purchasePrice: 0,
    bankFeePercentage: 0,
    bankFeeAmount: 0,
    totalCommissionPercentage: 0,
    totalCommissionAmount: 0,
    totalRefinance: 0,
    totalExternalPayoff: 0,
    leverage: 0,
    totalFees: 0,
    specifiedPercentage: 0,
    fundingDate: new Date().toISOString().split("T")[0],
  });

  const [debits, setDebits] = useState<any>({
    daily: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    weekly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    monthly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
  });

  const isLoading = false;

  useEffect(() => {
    const source =
      tabOptions.length > 0
        ? tabOptions.find((o) => o.value === selectedAnalysisTab)?.label || tabOptions[0].label
        : null;
    setSelectedSource(source);
    if (!source) return;
    if (source === "Ocrolus") return;
    if (source === "MoneyThumb" || source === "Lend Pathway") {
      fetchScoreboard(source);
    }
  }, [selectedAnalysisTab]);

  useEffect(() => {
    const source =
      tabOptions.length > 0
        ? tabOptions.find((o) => o.value === selectedAnalysisTab)?.label || tabOptions[0].label
        : null;
    setSelectedSource(source);
    if (source === "MoneyThumb" || source === "Lend Pathway" || source === "Ocrolus") return;
    setScoreboard(null);
    calculateScoreboardData(source);
  }, [bankStatementsSummary, positionCount, advancesData, selectedAnalysisTab]);

  const handleTabChange = async (newValue) => {
    setSelectedTab(newValue.value);

    const source = tabOptions.find((o) => o.value === newValue.value)?.label;
    if (!source) return;

    refreshDataBasedOnSource?.(source);

    if (source === "MoneyThumb" || source === "Lend Pathway") {
      await fetchScoreboard(source);
      return;
    }

    if (source === "Ocrolus") {
      await fetchOcrolusScoreboard();
      return;
    }

    calculateScoreboardData(source);
  };

  const fetchOcrolusScoreboard = async () => {
    try {
      setScoreboardLoading(true);

      const res = await get(`integration/ocrolous/book/${dealApplicationId}/booksummary`);

      if (!res || res.status !== "success") {
        setScoreboard(null);
        return;
      }

      setScoreboard(parseOcrolusScoreboard(res, AVAILABLE_HOLDBACK, positionCount));
    } finally {
      setScoreboardLoading(false);
    }
  };

  const parseOcrolusScoreboard = (response, availableHoldbackLimit) => {
    const data = response?.data || {};

    const num = (v) => Number(v) || 0;

    const avgMonthlyRevenue = num(data.averageMonthlyRevenue);
    const avgTrueDeposits = num(data.averageTrueDeposits);
    const avgMonthlyExpenses = num(data.averageMonthlyExpenses);
    const avgGrossProfit = num(data.averageGrossProfit);
    const totalMonthlyDebits = num(data.averageMonthlyDebits);
    const positionCt = num(data.positions);

    const monthlyMCAHoldback =
      avgTrueDeposits > 0 ? Number(((totalMonthlyDebits / avgTrueDeposits) * 100).toFixed(2)) : 0;

    const availableHoldBack = Number((availableHoldbackLimit - monthlyMCAHoldback).toFixed(2));

    return {
      averageMonthlyRevenue: avgMonthlyRevenue,
      averageTrueDeposits: avgTrueDeposits,
      averageNonTrueDeposits: avgMonthlyRevenue - avgTrueDeposits,
      averageMonthlyExpenses: avgMonthlyExpenses,
      averageGrossProfit: avgGrossProfit,
      totalMonthlyDebits,
      monthlyMCAHoldback,
      availableHoldBack,
      positions: positionCt,
    };
  };

  const parseMoneyThumbScoreboard = (response, availableHoldbackLimit) => {
    const stats = response?.result?.["Revenue Statistics"] || [];
    const mcaCompanies = response?.result?.["MCA Companies"] || [];

    const find = (label) => stats.find((i) => i.Label?.toLowerCase() === label.toLowerCase());

    const num = (v) => (v ? Number(String(v).replace(/[^0-9.-]/g, "")) || 0 : 0);

    const revenue = find("Revenue");
    const trueRevenue = find("True Revenue");
    const expenses = find("Expenses");
    const profit = find("Profit");
    const nonTrueRevenue = find("Non-True Revenue");
    const debt = find("Total Debt Withdrawals");
    const mcaHold = find("MCA Withhold Percent");

    const mca = num(mcaHold?.Annual);
    const available = Number((availableHoldbackLimit - mca).toFixed(2));

    const positions = (mcaCompanies || []).length;

    return {
      averageMonthlyRevenue: num(revenue?.Monthly),
      averageTrueDeposits: num(trueRevenue?.Monthly),
      averageNonTrueDeposits: num(nonTrueRevenue?.Monthly),
      averageMonthlyExpenses: num(expenses?.Monthly),
      averageGrossProfit: num(profit?.Monthly),
      totalMonthlyDebits: num(debt?.Monthly),
      monthlyMCAHoldback: mca,
      availableHoldBack: available,
      positions: positions,
    };
  };

  const parseLendPathwayScoreboard = (response, availableHoldbackLimit) => {
    const scoreboard = response?.data?.scoreboard;
    if (!scoreboard) return null;

    const statements = scoreboard.statements || [];
    if (statements.length === 0) return null;

    const deposits = scoreboard.total_deposits || 0;
    const trueRevenue = scoreboard.true_revenue || 0;
    const withdrawals = scoreboard.total_withdrawals || 0;

    const loanSummary = scoreboard.loan_summary || [];
    const mcaLoan = loanSummary.find((l) => l.loan_type === "merchant_cash_advance");
    const loanPayments = mcaLoan?.total_payments || 0;

    const months = statements.length || 1;

    const avgMonthlyRevenue = deposits / months;
    const avgTrueRevenue = trueRevenue / months;
    const avgMonthlyExpenses = withdrawals / months;
    const avgGrossProfit = avgMonthlyRevenue - avgMonthlyExpenses;

    const totalMonthlyDebits = loanPayments / months;

    const mcaPaymentRatio =
      trueRevenue > 0 ? Number(((loanPayments / trueRevenue) * 100).toFixed(2)) : 0;

    const availableHoldBack = Number((availableHoldbackLimit - mcaPaymentRatio).toFixed(2));

    const positions = (scoreboard.positions || []).filter(
      (p) => p?.loan_type?.toLowerCase() === "merchant_cash_advance"
    ).length;

    const averageNonTrueDeposits = avgMonthlyRevenue - avgTrueRevenue;

    return {
      averageMonthlyRevenue: avgMonthlyRevenue,
      averageTrueDeposits: avgTrueRevenue,
      averageNonTrueDeposits: averageNonTrueDeposits,
      averageMonthlyExpenses: avgMonthlyExpenses,
      averageGrossProfit: avgGrossProfit,
      totalMonthlyDebits,
      monthlyMCAHoldback: mcaPaymentRatio,
      availableHoldBack,
      positions,
    };
  };

  const fetchScoreboard = async (source) => {
    try {
      setScoreboardLoading(true);
  
      if (source === "Lend Pathway") {
        const res = await get(
          `integration/lend-pathway/getScoreboard/${dealApplicationId}`
        );
  
        if (!res || res.status !== "success") {
          setScoreboard(null);
          return;
        }
  
        setScoreboard(parseLendPathwayScoreboard(res, AVAILABLE_HOLDBACK));
        return;
      }
  
      if (source === "MoneyThumb") {
        const res = await get(
          `moneythumb/getStatementsScoreboard/${dealApplicationId}`
        );
  
        if (!res) {
          setScoreboard(null);
          return;
        }
  
        setScoreboard(parseMoneyThumbScoreboard(res, AVAILABLE_HOLDBACK));
        return;
      }
  
      setScoreboard(null);
    } finally {
      setScoreboardLoading(false);
    }
  };
  const calculateScoreboardData = useCallback(
    (source?: string) => {
      if (bankStatementsSummary && bankStatementsSummary.length > 0) {
        let totalRevenue = 0;
        let totalTrueDeposits = 0;
        let totalNonTrueDeposits = 0;
        let totalExpenses = 0;
        let totalGrossProfit = 0;
        let mcaHoldback = 0;
        let totalMonthlyDebits = 0;
        const effectiveSource = source ?? selectedSource;

        advancesData
          .filter((adv) => adv?.selected)
          .forEach((advance: any) => {
            totalMonthlyDebits += convertNumeric(advance.monthlyAmount) || 0;
          });

        bankStatementsSummary
          .filter((summary: any) => summary.selected)
          .forEach((statement: any) => {
            totalRevenue += convertNumeric(statement.totalCredits) || 0;
            totalTrueDeposits += convertNumeric(statement.totalTrueCredits) || 0;
            totalNonTrueDeposits +=
              convertNumeric(statement.totalCredits) - convertNumeric(statement.totalTrueCredits) ||
              0;
            totalExpenses += convertNumeric(statement.totalDebits) || 0;
            totalGrossProfit +=
              convertNumeric(statement.totalCredits) - convertNumeric(statement.totalDebits) || 0;
          });

        const numStatements = (
          bankStatementsSummary?.filter((summary: any) => summary.selected) || []
        ).length;

        const averageTrueDeposits = numStatements > 0 ? totalTrueDeposits / numStatements : 0;
        let averageMonthlyDebits = 0;

        if (numStatements > 0) {
          averageMonthlyDebits = Math.round((totalMonthlyDebits / numStatements) * 100) / 100;
        } else {
          averageMonthlyDebits = totalMonthlyDebits;
        }

        if (averageTrueDeposits > 0) {
          mcaHoldback = (averageMonthlyDebits / averageTrueDeposits) * 100;
          mcaHoldback = parseFloat(mcaHoldback.toFixed(2));
        }

        setScoreboard({
          averageMonthlyRevenue:
            numStatements > 0 ? Math.round((totalRevenue / numStatements) * 100) / 100 : 0,
          averageTrueDeposits:
            numStatements > 0 ? Math.round((totalTrueDeposits / numStatements) * 100) / 100 : 0,
          averageNonTrueDeposits:
            numStatements > 0 ? Math.round((totalNonTrueDeposits / numStatements) * 100) / 100 : 0,
          averageMonthlyExpenses:
            numStatements > 0 ? Math.round((totalExpenses / numStatements) * 100) / 100 : 0,
          averageGrossProfit:
            numStatements > 0 ? Math.round((totalGrossProfit / numStatements) * 100) / 100 : 0,
          totalMonthlyDebits: Math.round(averageMonthlyDebits * 100) / 100,
          monthlyMCAHoldback: Math.round(mcaHoldback * 100) / 100,
          availableHoldBack:
            Math.round(((AVAILABLE_HOLDBACK || 0) - (mcaHoldback ? mcaHoldback : 0)) * 100) / 100,
          positions:
            effectiveSource == "Ocrolus"
              ? positionCount
              : advancesData?.filter((adv) => adv.fundedAmount && adv.fundedAmount > 0).length,
        });
      }
    },
    [bankStatementsSummary, advancesData, selectedSource, positionCount, AVAILABLE_HOLDBACK]
  );

  const customButton = (
    <MDBox display="flex" alignItems="center" gap={2}>
      {allowResubmit && (
        <MDBox>
          <Tooltip title="Add Statements" placement="bottom" arrow>
            <Add
              onClick={onResubmit}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontSize: "25px !important",
                "& svg": {
                  strokeWidth: 3,
                  stroke: "currentColor",
                  filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.1))",
                },
                "&:hover": {
                  color: "primary.dark",
                  "& svg": {
                    strokeWidth: 5,
                  },
                },
              }}
            />
          </Tooltip>
        </MDBox>
      )}

      {allowSummarySelection && (
        <MDSelect
          label=""
          value={(() => {
            if (!tabOptions || tabOptions.length === 0) return "";

            const selectedOption =
              tabOptions.find((o) => o.value === selectedAnalysisTab) || tabOptions[0];

            const isDecisionLogic = selectedOption?.label === "Decision Logic";

            const finalValue = isDecisionLogic
              ? tabOptions.find((o) => o.value === prevSelectedAnalysisTab) || tabOptions[0]
              : selectedOption;

            return finalValue || "";
          })()}
          options={tabOptions}
          onChange={handleTabChange}
          loading={isLoading}
          disableClearable={true}
          freeSolo={false}
          getOptionLabel={(option) => option.label}
          renderInputProps={{ sx: { fontSize: "14px", fontWeight: 500 } }}
          sx={{ width: "200px", marginBottom: "20px" }}
        />
      )}

      {allowButton && (
        <MDButton
          color="primary"
          variant="contained"
          size="medium"
          sx={{ textTransform: "none" }}
          onClick={() => {
            setScheduleFrequency("weekly");
            setOpen(true);
          }}
        >
          Funding Calculator
        </MDButton>
      )}
    </MDBox>
  );

  return (
    <>
      <MDBox position="relative">
        {scoreboardLoading && (
          <MDBox
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={20}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              backgroundColor: "rgba(255,255,255,0.7)",
              cursor: "not-allowed",
            }}
          >
            <Loader />
          </MDBox>
        )}

        <MDBox
          sx={{
            pointerEvents: scoreboardLoading ? "none" : "auto",
            opacity: scoreboardLoading ? 0.4 : 1,
          }}
        >
          <CollapsibleCard header={"Summary"} customButton={customButton}>
            <MDBox
              paddingLeft={4}
              paddingRight={3}
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <HeaderSection
                label="Average Monthly Revenue"
                value={formatCurrencyUSD(scoreboard?.averageMonthlyRevenue)}
              />

              <HeaderSection
                label="Average True Deposits"
                value={formatCurrencyUSD(scoreboard?.averageTrueDeposits)}
              />

              <HeaderSection
                label="Total Monthly Debits"
                value={formatCurrencyUSD(scoreboard?.totalMonthlyDebits)}
              />

              <HeaderSection
                label="Average Non-True Deposits"
                value={formatCurrencyUSD(scoreboard?.averageNonTrueDeposits)}
              />

              <HeaderSection
                label="Average Monthly Expenses"
                value={formatCurrencyUSD(scoreboard?.averageMonthlyExpenses)}
              />

              <HeaderSection
                label="Average Gross Profit"
                value={formatCurrencyUSD(scoreboard?.averageGrossProfit)}
              />

              <HeaderSection
                label="MCA Holdback"
                value={scoreboard?.monthlyMCAHoldback ? `${scoreboard?.monthlyMCAHoldback}%` : ""}
              />

              <HeaderSection
                label="Available Holdback"
                value={scoreboard?.availableHoldBack ? `${scoreboard?.availableHoldBack}%` : ""}
              />

              <HeaderSection label="Positions" value={scoreboard?.positions} />
            </MDBox>
          </CollapsibleCard>
        </MDBox>
      </MDBox>

      {open && (
        <FundingChart
          open={open}
          setOpen={setOpen}
          mode="add"
          formData={formData}
          setFormData={setFormData}
          debits={debits}
          scoreboard={scoreboard}
          setDebits={setDebits}
          dealApplicationId={dealApplicationId}
          scheduleFrequency={scheduleFrequency}
          setScheduleFrequency={setScheduleFrequency}
        />
      )}
    </>
  );
};

export default SummaryCard;
